// Shared types for the input-island React components.
//
// Each input island mounts into a slot the Shiny side renders for a
// `groups:`-rail group whose `kind` requires the React treatment
// (today: `curve_editor`, `bracket_edit`). Props are read from `data-*`
// attributes on the mount element — see `mount.tsx` for the reader
// functions. Arrays are JSON-encoded in the attribute string.

export interface DeltaSliderProps {
  /** Namespaced Shiny input id. The slider writes its current value to
   *  this id via Shiny.setInputValue so the server reads it through the
   *  existing groups-rail panel reactives — identical contract to the
   *  bslib sliderInput it replaces. */
  inputId: string;
  /** User-facing slider label. */
  label: string;
  /** Parent instrument's display title (e.g. "Personal Income Tax"); used to
   *  group the control under its instrument in the "Your reform" modal (#65). */
  instrument: string;
  /** Optional one-liner shown next to the label as a tooltip. */
  description?: string;
  /** Slider default value. For the scalar_multiplier %-delta semantics
   *  this is 0 (identity / no change); the delta-bar fill collapses there. */
  defaultValue: number;
  min: number;
  max: number;
  step: number;
  /** Unit suffix shown after the value readout (e.g. "%"). */
  postfix?: string;
  /** Optional mirror callback fired on every value change (incl. reset).
   *  Lets a host component (e.g. CurveEditor) track the value for a
   *  decoration chart without owning the state or the Shiny write. */
  onValueChange?: (value: number) => void;
}

/** How an absolute slider formats its readout — derived R-side from the
 *  parameter's `type`/`unit` and passed through the mount as `data-format`. */
export type AbsoluteFormat = "euro" | "percent" | "number";

export interface AbsoluteSliderProps {
  /** Namespaced Shiny input id. The slider writes its current value to this id
   *  via Shiny.setInputValue so the server reads it through the existing
   *  groups-rail panel reactive at input[[param$id]] — identical contract to
   *  the bslib sliderInput it replaces. */
  inputId: string;
  /** User-facing slider label. */
  label: string;
  /** Parent instrument's display title (e.g. "Personal Income Tax"); used to
   *  group the control under its instrument in the "Your reform" modal (#65). */
  instrument: string;
  /** Optional one-liner shown next to the label as a tooltip. */
  description?: string;
  /** Slider default value (the parameter baseline). The baseline marker sits
   *  here and the value is identity at this point (no modification emitted). */
  defaultValue: number;
  min: number;
  max: number;
  step: number;
  /** Readout format: euro ("€9,050"), percent ("21%"), or plain number. */
  format: AbsoluteFormat;
  /** Optional mirror callback fired on every value change (incl. reset). */
  onValueChange?: (value: number) => void;
}

export interface CurveEditorProps {
  /** Namespaced Shiny input id. The island writes its committed value
   *  to this id via Shiny.setInputValue so the server reads it through
   *  the existing groups-rail panel reactives. */
  inputId: string;
  /** User-facing slider label. */
  label: string;
  /** Parent instrument's display title (e.g. "Personal Income Tax"); used to
   *  group the control under its instrument in the "Your reform" modal (#65). */
  instrument: string;
  /** Optional one-liner shown next to the label as a tooltip. */
  description?: string;
  /** Slider default value. Per the spec contract this is always 0 for
   *  the percentage-delta semantics (identity at no-change). */
  defaultValue: number;
  min: number;
  max: number;
  step: number;
  /** Backend parameter names the slider scales. Read from `data-targets`;
   *  not currently displayed (the engine resolves targets R-side from the
   *  spec). Kept on the props for parity with the mount/data contract. */
  targets: string[];
  /** Baseline curve x-axis values (e.g. fiscal-power brackets). */
  xAxisData: number[];
  /** Baseline curve y-axis values (e.g. annual CT amounts per bracket). */
  yAxisData: number[];
  xAxisLabel: string;
  yAxisLabel: string;
}

export interface BracketEditorProps {
  /** Namespaced Shiny input id. The island commits a value-shaped
   *  object { boundaries: number[], rates: number[] } via
   *  Shiny.setInputValue so the server reads it through the existing
   *  groups-rail panel reactives. */
  inputId: string;
  label: string;
  /** Parent instrument's display title; groups the control under its instrument
   *  in the "Your reform" modal (#65). */
  instrument: string;
  description?: string;
  /** Backend param names for the editable bracket boundaries. The
   *  leftmost rate has no editable boundary (chart anchors at x=0), so
   *  boundary_targets.length === rate_targets.length - 1. */
  boundaryTargets: string[];
  rateTargets: string[];
  /** Default values matching the targets arrays. Reset and identity
   *  checks compare against these. */
  boundaryDefaults: number[];
  rateDefaults: number[];
  /** Domain bounds for the x (income) and y (rate) axes. Defaults are
   *  required to lie within. */
  boundaryMin: number;
  boundaryMax: number;
  boundaryStep: number;
  rateMin: number;
  rateMax: number;
  rateStep: number;
  xAxisLabel: string;
  yAxisLabel: string;
}

/** A selectable VAT band (the surviving taxed rates: 21% / 12% / 6%). */
export interface CategoricalBand {
  id: string;
  /** Statutory rate as a fraction, e.g. 0.21. */
  rate: number;
}

/** One goods category the user can re-band. `targets` (the backend params)
 *  live R-side only — the island just needs the display fields and the
 *  baseline band. `defaultBand` is a band id or the "exempt" sentinel (0%
 *  baseline goods, which can be moved onto a band but not set back to exempt
 *  here). */
export interface CategoricalCategory {
  id: string;
  label: string;
  section: string;
  defaultBand: string;
}

export interface CategoricalAssignmentProps {
  /** Namespaced Shiny input id. The island commits a record
   *  { [categoryId]: bandId } of the re-banded categories (modified only) via
   *  Shiny.setInputValue so the server reads it through the existing
   *  groups-rail panel reactive at input[[group$id]]. */
  inputId: string;
  label: string;
  /** Parent instrument's display title; groups the control under its instrument
   *  in the "Your reform" modal (#65). */
  instrument: string;
  description?: string;
  bands: CategoricalBand[];
  categories: CategoricalCategory[];
}

/** The "exempt" baseline sentinel — a default band that is shown but not
 *  selectable (the zero-rate band was removed in #136). */
export const EXEMPT_BAND = "exempt";

// Shiny's window-global type is centralised in lib/shiny.ts so every
// React-side consumer agrees on the surface.
import "../../lib/shiny";

export {};
